import React, { useState, useEffect } from "react";
import { format, parse } from "date-fns";

interface DatePickerProps {
  placeholder: string;
  defaultValue?: string;
  onValueChange: (dateString: string) => void;
  dateFormat?: string;
}

const DatePicker = ({
  placeholder,
  defaultValue = "",
  onValueChange,
  dateFormat = "dd-MMM-yyyy"
}: DatePickerProps) => {
  const [displayValue, setDisplayValue] = useState(defaultValue);

  useEffect(() => {
    setDisplayValue(defaultValue);
  }, [defaultValue]);

  const formatDateForInput = (dateString: string): string => {
    if (!dateString) return "";

    // If it's already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }

    // Otherwise parse from display format
    const parseDate = (str: string) => parse(str, dateFormat, new Date());
    const formatToISO = (date: Date) => format(date, "yyyy-MM-dd");
    const parsedDate = parseDate(dateString);

    return parsedDate instanceof Date && !isNaN(parsedDate.getTime())
      ? formatToISO(parsedDate)
      : "";
  };

  const formatDateForOutput = (inputDate: string): string => {
    if (!inputDate) return "";

    // Parse YYYY-MM-DD directly to avoid timezone issues
    const parts = inputDate.split("-");
    if (parts.length !== 3) return "";

    const year = parseInt(parts[0] || "", 10);
    const month = parseInt(parts[1] || "", 10);
    const day = parseInt(parts[2] || "", 10);

    const isValidNumbers = !isNaN(year) && !isNaN(month) && !isNaN(day);
    if (!isValidNumbers) return "";

    const date = new Date(year, month - 1, day);
    const formatToDisplay = (d: Date) => format(d, dateFormat);

    return date instanceof Date && !isNaN(date.getTime()) ? formatToDisplay(date) : "";
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    if (!inputValue) {
      setDisplayValue("");
      onValueChange("");
      return;
    }

    const formattedDate = formatDateForOutput(inputValue);
    setDisplayValue(formattedDate);
    onValueChange(formattedDate);
  };

  return (
    <input
      type="date"
      style={{
        borderRadius: "0px",
        borderWidth: "1px",
        borderColor: "#ccc",
        borderStyle: "solid",
        width: "100%",
        height: "38px",
        padding: "8px 12px",
        fontSize: "14px"
      }}
      placeholder={placeholder}
      value={formatDateForInput(displayValue)}
      onChange={handleChange}
    />
  );
};

export default DatePicker;
